import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Mail, ArrowRight, Loader2, XCircle, Download, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, API } from "@/lib/api";
import { useT } from "@/context/I18nContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { t, locale } = useT();
  const [status, setStatus] = useState({ payment_status: "pending", buyer_email: "", codes: [] });
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState(null);

  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setError("Missing session id"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status !== "paid" && attempts < 20) {
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        }
      } catch (err) {
        setError(err?.response?.data?.detail || "Errore");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, attempts]);

  const downloadPdf = (code) => {
    window.open(`${API}/payments/gift-card/${sessionId}/${code}`, "_blank");
  };

  return (
    <div className="min-h-screen text-slate-100">
      <main className="mx-auto flex min-h-screen max-w-lg items-center justify-center px-4 py-10">
        <div className="w-full rounded-2xl border border-slate-800 bg-slate-900/60 p-8 text-center glow-cyan">
          {error ? (
            <>
              <XCircle className="mx-auto h-16 w-16 text-red-400" />
              <h1 className="mt-4 text-2xl font-bold">{locale === "en" ? "Error" : "Errore"}</h1>
              <p className="mt-2 text-slate-400">{error}</p>
            </>
          ) : status.payment_status === "paid" ? (
            <>
              <CheckCircle2 className="mx-auto h-16 w-16 text-cyan-300" data-testid="payment-success-icon" />
              <h1 className="mt-4 text-2xl font-bold">
                {locale === "en" ? "Payment successful!" : "Pagamento riuscito!"}
              </h1>
              <p className="mt-2 text-slate-300">
                {locale === "en" ? "We emailed your redemption code" : "Abbiamo inviato il tuo codice a"}{" "}
                {status.codes.length > 1 && (locale === "en" ? "s" : "i")}
                {locale === "en" ? " to " : ""}
                <br />
                <strong className="text-cyan-300">{status.buyer_email}</strong>
              </p>

              {status.codes && status.codes.length > 0 && (
                <div data-testid="codes-list" className="mt-6 space-y-2 text-left">
                  <p className="text-xs uppercase tracking-widest text-slate-400">
                    {locale === "en" ? "Your codes" : "I tuoi codici"} ({status.codes.length})
                  </p>
                  {status.codes.map((c) => (
                    <div
                      key={c}
                      data-testid={`code-row-${c}`}
                      className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/60 p-3"
                    >
                      <code className="mono flex-1 text-lg font-bold tracking-widest text-cyan-300">{c}</code>
                      <Button
                        size="sm"
                        data-testid={`gift-pdf-${c}`}
                        onClick={() => downloadPdf(c)}
                        className="bg-cyan-400 text-slate-950 hover:bg-cyan-300"
                      >
                        <Download className="mr-1 h-3.5 w-3.5" /> PDF
                      </Button>
                    </div>
                  ))}
                  <p className="mt-2 text-[11px] text-slate-500">
                    {locale === "en"
                      ? "Print the PDF to gift the code in person."
                      : "Stampa il PDF per regalare il codice di persona."}
                  </p>
                </div>
              )}

              <div className="mt-6 flex items-start gap-2 rounded-xl border border-slate-800 bg-slate-950/60 p-4 text-left text-sm text-slate-300">
                <Mail className="mt-0.5 h-4 w-4 text-cyan-300" />
                <div>
                  {locale === "en"
                    ? "Also check your inbox (and spam). Support: "
                    : "Controlla anche la casella email (e spam). Supporto: "}
                  <a href="mailto:st.support25@gmail.com" className="text-cyan-300 underline">st.support25@gmail.com</a>.
                </div>
              </div>
              <Button
                onClick={() => nav("/redeem")}
                data-testid="go-redeem-btn"
                className="mt-6 w-full bg-cyan-400 text-slate-950 hover:bg-cyan-300"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                {locale === "en" ? "Go to redeem code" : "Vai al riscatto codice"} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Loader2 className="mx-auto h-16 w-16 animate-spin text-cyan-300" />
              <h1 className="mt-4 text-2xl font-bold">
                {locale === "en" ? "Confirming payment..." : "Conferma pagamento..."}
              </h1>
              <p className="mt-2 text-slate-400">
                {locale === "en"
                  ? "Please wait while we confirm your payment."
                  : "Attendi qualche istante mentre confermiamo il tuo pagamento."}
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
